import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Typography, Tabs, Tab, Chip,
  Avatar, CircularProgress, useMediaQuery, useTheme, Container,
} from '@mui/material';
import ConstructionBanner from '../components/ConstructionBanner';
import Navbar from '../components/Navbar';
import AdminViewBar from '../components/AdminViewBar';
import TierGate from '../components/TierGate';
import { useAuth } from '../context/AuthContext';

import PlayerStatsTable from '../components/Dashboard/PlayerStatsTable';
import TeamStatsTable from '../components/Dashboard/TeamStatsTable';
import CapEfficiencyTable from '../components/Dashboard/CapEfficiencyTable';
import FreeAgentExplorer from '../components/Dashboard/FreeAgentExplorer';
import DraftExplorer from '../components/Dashboard/DraftExplorer';

import MetricsGlossary from '../components/MetricsGlossary';
import DefensiveNR from '../components/NFLPlayers/DefensiveNR';
import DefensiveTeamNR from '../components/NFLPlayers/DefensiveTeamNR';
import OffensiveNR from '../components/NFLPlayers/OffensiveNR';
import OffensiveTeamNR from '../components/NFLPlayers/OffensiveTeamNR';
import TeamNROverall from '../components/NFLPlayers/TeamNROverall';

import ThreeColumnLayout from '../components/sports/ThreeColumnLayout';
import { NFLCompactStandings } from '../components/sports/CompactStandings';
import { NFLGamesPanel } from '../components/sports/GamesPanel';
import { NFLNewsGrid } from '../components/sports/CuratedNews';

const NAVBAR_H = 56;
const SUBNAV_H = 48;

const NFL_STRUCTURE = {
  AFC: {
    'AFC East':  [2, 15, 17, 20],
    'AFC North': [33, 4, 5, 23],
    'AFC South': [34, 11, 30, 10],
    'AFC West':  [7, 12, 13, 24],
  },
  NFC: {
    'NFC East':  [6, 19, 21, 28],
    'NFC North': [3, 8, 9, 16],
    'NFC South': [1, 29, 18, 27],
    'NFC West':  [22, 14, 26, 25],
  },
};

function getStat(stats, name) {
  return (stats || []).find(s => s.name === name)?.value ?? 0;
}

// ── Tab 0: News ───────────────────────────────────────────────────────────────
function NewsTab() {
  return (
    <ThreeColumnLayout
      left={<NFLCompactStandings />}
      center={<NFLNewsGrid />}
      right={<NFLGamesPanel />}
    />
  );
}

// ── Tab 1: Standings ─────────────────────────────────────────────────────────
function StandingsTab() {
  const navigate = useNavigate();
  const [teamMap, setTeamMap] = useState({});
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    const allIds = Object.values(NFL_STRUCTURE).flatMap(conf => Object.values(conf).flat());
    Promise.all(
      allIds.map(id =>
        axios.get(`https://site.api.espn.com/apis/site/v2/sports/football/nfl/teams/${id}`)
          .then(r => {
            const t = r.data?.team || {};
            const rec = (t.record?.items || []).find(i => i.type === 'total') || {};
            const stats = rec.stats || [];
            return {
              id: Number(t.id),
              color: t.color,
              logo: t.logos?.[0]?.href,
              city: t.location || '',
              nickname: t.nickname || t.name || t.shortDisplayName || t.displayName,
              wins: Math.round(getStat(stats, 'wins')),
              losses: Math.round(getStat(stats, 'losses')),
              ties: Math.round(getStat(stats, 'ties')),
              pct: getStat(stats, 'winPercent'),
            };
          }).catch(() => null)
      )
    ).then(results => {
      const map = {};
      results.filter(Boolean).forEach(t => { map[t.id] = t; });
      setTeamMap(map);
      setLoading(false);
    });
  }, []);

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
      <CircularProgress color="primary" />
      <Typography variant="caption" sx={{ color: '#555' }}>Loading all 32 teams…</Typography>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 3 }, flexDirection: { xs: 'column', md: 'row' } }}>
      {Object.entries(NFL_STRUCTURE).map(([conf, divisions]) => (
        <Box key={conf} sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(245,166,35,0.25)' }} />
            <Typography variant="h5" sx={{ fontWeight: 800, px: 1 }}>{conf}</Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(245,166,35,0.25)' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {Object.entries(divisions).map(([divName, ids]) => {
              const sorted = ids.map(id => teamMap[id]).filter(Boolean)
                .sort((a, b) => b.pct - a.pct || a.losses - b.losses);
              return (
                <Box key={divName} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: '#f5a623', fontWeight: 700, fontSize: '0.62rem', letterSpacing: '0.07em', display: 'block', mb: 1, textAlign: 'center' }}>
                    {divName.replace(/AFC |NFC /, '')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {sorted.map((team, rank) => {
                      const color = team.color ? `#${team.color}` : '#2ea84b';
                      return (
                        <Box key={team.id} onClick={() => navigate(`/team/${team.id}`)} sx={{
                          cursor: 'pointer', borderRadius: 2, border: `1px solid ${color}33`,
                          backgroundColor: '#131318', p: { xs: 0.5, lg: 0.75 },
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3,
                          transition: 'transform 0.12s, border-color 0.12s',
                          '&:hover': { borderColor: color, transform: 'translateY(-2px)', backgroundColor: '#1c1c24' },
                        }}>
                          <Chip label={`#${rank + 1}`} size="small" sx={{ fontSize: '0.52rem', height: 14, bgcolor: color + '33', color: '#fff', fontWeight: 700 }} />
                          {team.logo && <Avatar src={team.logo} alt={team.nickname} sx={{ width: isLg ? 34 : 28, height: isLg ? 34 : 28, borderRadius: 0 }} />}
                          <Typography sx={{ color: '#9fb5c9', fontWeight: 400, fontSize: '0.58rem', lineHeight: 1.1, textAlign: 'center' }}>{team.city}</Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.68rem', color: '#fff', lineHeight: 1.1, textAlign: 'center' }}>{team.nickname}</Typography>
                          <Typography sx={{ color: '#9eb8d0', fontSize: '0.6rem' }}>{team.wins}–{team.losses}{team.ties > 0 ? `–${team.ties}` : ''}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ── Tab 2: Player Stats ───────────────────────────────────────────────────────
function PlayerStatsTab() {
  return (
    <TierGate tier="free" feature="Player Stats & Nerd Ratings">
      <PlayerStatsTable />
    </TierGate>
  );
}

// ── Tab 3: Team Stats ─────────────────────────────────────────────────────────
function TeamStatsTab() {
  return (
    <TierGate tier="free" feature="Team Stats">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#e8f0f8' }}>Team Stats</Typography>
        <Chip label="🔓 Free" size="small" sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20, bgcolor: '#0a2a14', color: '#2ea84b', border: '1px solid #2ea84b55' }} />
        <Chip label="⚡ Advanced — Nerds+" size="small" sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20, bgcolor: '#2a1a05', color: '#f5a623', border: '1px solid #f5a62355' }} />
      </Box>
      <TeamStatsTable />
    </TierGate>
  );
}

// ── Tab 4: Coming Soon ────────────────────────────────────────────────────────
// live: true  → shown to all users (row-level gates handle conversion)
// live: false → admin-only (in development)
const COMING_SOON_ITEMS = [
  { emoji: '🧳', label: 'Free Agent Explorer',   Component: FreeAgentExplorer, live: true,  tier: 'nerds+', desc: 'Browse available free agents by position, age, and contract value — with Nerd Rating scores and advanced metrics to find hidden value. Includes Consensus FA Board (top 10 free).' },
  { emoji: '🎓', label: 'NFL Draft Explorer',    Component: DraftExplorer,     live: true,  tier: 'free',   desc: 'Full 2026 draft order (all 7 rounds) with consensus big board ranking prospects from DJ and Kiper. Top 15 free.' },
  { emoji: '💰', label: 'Cap Efficiency Rating', Component: CapEfficiencyTable, live: true, tier: 'nerds+', desc: 'Which players deliver the most production per cap dollar. Performance metrics stacked against cap hit to surface the best and worst value contracts.' },
  { emoji: '⚖️', label: 'Position Comparison',      live: false, tier: 'nerds+', desc: 'Compare any two players at the same position side-by-side — QBs, WRs, RBs, DBs — across every advanced metric.' },
  { emoji: '🔍', label: 'Situational Play Explorer', live: false, tier: 'nerds+', desc: 'Filter any play situation — down, distance, formation, field zone — and instantly see EPA, success rate, and a full 32-team rankings table.' },
  { emoji: '🗺️', label: 'Drive EPA Explorer',        live: false, tier: 'nerds+', desc: 'EPA/play and success rate filtered by drive starting field position — see exactly where offenses click and where they stall.' },
  { emoji: '🎯', label: 'Situational QB Splits',     live: false, tier: 'nerds+', desc: 'Any QB, 15 splits: shotgun vs. under center, clean pocket vs. pressure, by down, quarter, and game script.' },
  { emoji: '🔴', label: 'Red Zone Tendencies',       live: false, tier: 'nerds+', desc: 'Pass%, run%, EPA/play, TD rate, and goal-to-go behavior — all 32 teams, sort by any column.' },
  { emoji: '📐', label: 'Team Style Profiles',       live: false, tier: 'nerds+', desc: 'Radar charts of offensive and defensive identity — run-heavy, explosive, conservative, high-variance.' },
  { emoji: '📈', label: 'Win Probability',            live: false, tier: 'nerds+', desc: 'In-game win probability curves for any matchup, with key momentum swing plays highlighted.' },
  { emoji: '🏆', label: 'Fantasy Projections',       live: false, tier: 'nerds+', desc: 'Data-driven weekly projections built on EPA, snap share, and target share — not just gut feel.' },
  { emoji: '🌳', label: 'Route Tree Analysis',        live: false, tier: 'nerds+', desc: 'See which routes each WR/TE runs most, EPA by route type, and how defenses scheme against them.' },
  { emoji: '⚗️', label: 'Theory Tester',              live: false, tier: 'nerds+', desc: 'Build and test your own football hypotheses against real play-by-play data.' },
];

// eslint-disable-next-line no-unused-vars
const TIER_CHIP = {
  'free':   { label: 'Free',   sx: { bgcolor: '#0a2a14', color: '#2ea84b', border: '1px solid #2ea84b55' } },
  'nerds+': { label: 'Nerds+', sx: { bgcolor: '#2a1a05', color: '#f5a623', border: '1px solid #f5a62355' } },
};

function ComingSoonTab() {
  const { isAdmin } = useAuth();
  // Non-admins only see live tools; admins see everything including in-dev placeholders
  const visibleItems = isAdmin ? COMING_SOON_ITEMS : COMING_SOON_ITEMS.filter(t => t.live);
  const [toolIdx, setToolIdx] = useState(0);
  // Reset selection if it goes out of bounds (e.g. admin → free view)
  const safeIdx = Math.min(toolIdx, visibleItems.length - 1);
  const active = visibleItems[safeIdx];

  if (!active) return null;

  const tierChip = active.tier === 'free'
    ? <Chip label="Free" size="small" sx={{ fontWeight: 800, fontSize: '0.62rem', height: 20, bgcolor: '#0a2a14', color: '#2ea84b', border: '1px solid #2ea84b55' }} />
    : <Chip label="Nerds+" color="secondary" size="small" sx={{ fontWeight: 800, fontSize: '0.68rem' }} />;

  return (
    <>
      {/* Sub-nav */}
      <Box sx={{
        display: 'flex', gap: 0.5, flexWrap: 'wrap',
        px: 1.5, py: 1.25,
        borderBottom: '1px solid rgba(245,166,35,0.1)',
        backgroundColor: '#0a0f18',
      }}>
        {visibleItems.map((t, i) => (
          <Box key={t.label} onClick={() => setToolIdx(i)} sx={{
            display: 'flex', alignItems: 'center', gap: 0.6,
            px: 1.5, py: 0.5, borderRadius: 1.5, cursor: 'pointer',
            fontSize: '0.8rem', fontWeight: 700, userSelect: 'none',
            backgroundColor: safeIdx === i ? '#1a2a1a' : '#1a2535',
            color:            safeIdx === i ? '#2ea84b' : '#9eb8d0',
            border: `1px solid ${safeIdx === i ? 'rgba(46,168,75,0.4)' : '#2a3d52'}`,
            transition: 'all 0.12s',
            '&:hover': { backgroundColor: safeIdx === i ? '#1e321e' : '#22334a' },
          }}>
            <span>{t.emoji}</span>
            <span>{t.label}</span>
            {!t.live && isAdmin && (
              <Chip label="dev" size="small" sx={{ height: 14, fontSize: '0.5rem', fontWeight: 800, bgcolor: '#2a1a05', color: '#f5a62388', border: '1px dashed #f5a62333', ml: 0.5 }} />
            )}
          </Box>
        ))}
      </Box>

      {/* Selected tool */}
      <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 2, maxWidth: active.live ? '100%' : 820 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>{active.emoji} {active.label}</Typography>
          {tierChip}
          {active.live
            ? <Chip label="LIVE" size="small" sx={{ fontWeight: 800, fontSize: '0.62rem', height: 20, bgcolor: '#0a2a14', color: '#2ea84b', border: '1px solid #2ea84b55' }} />
            : <Chip label="In Development" size="small" sx={{ fontWeight: 800, fontSize: '0.62rem', height: 20, bgcolor: '#0a1a2a', color: 'rgba(245,166,35,0.4)', border: '1px dashed #2a4560' }} />
          }
        </Box>
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.88rem', mb: 2, lineHeight: 1.6 }}>{active.desc}</Typography>

        {active.live && active.Component ? (
          // Only gate unauthenticated (basic) users — free users see the component,
          // row-level teasers inside each chart handle the Nerds+ conversion funnel.
          <TierGate tier="free" feature={active.label}>
            <Box sx={{ backgroundColor: '#0d1825', border: '1px solid rgba(245,166,35,0.15)', borderRadius: 2, overflow: 'hidden' }}>
              <active.Component />
            </Box>
          </TierGate>
        ) : (
          <Box sx={{
            backgroundColor: '#0d1825', border: '1px dashed #2a4560', borderRadius: 2,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            py: 8, px: 3, textAlign: 'center',
          }}>
            <Typography sx={{ fontSize: '3rem', mb: 1.5 }}>{active.emoji}</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#e8f0f8', mb: 0.75 }}>
              {active.label} — Coming Soon
            </Typography>
            <Typography sx={{ color: '#4a6080', fontSize: '0.82rem', maxWidth: 400 }}>
              This tool is in development. It'll unlock for Nerds+ subscribers when it's ready.
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
}

// ── Nerd Ranks — inner tab bar ────────────────────────────────────────────────
const NR_INNER_TABS = [
  { label: '🏆 Team NR',       id: 'team-nr'     },
  { label: '⚔️ Off. Team NR',  id: 'off-team-nr' },
  { label: '🛡️ Def. Team NR',  id: 'def-team-nr' },
  { label: '⚔️ Offensive NR',  id: 'off-nr'      },
  { label: '🛡️ Defensive NR',  id: 'def-nr'      },
];

const NR_INNER_COMPONENTS = [
  () => <TeamNROverall />,
  () => <OffensiveTeamNR />,
  () => <DefensiveTeamNR />,
  () => <OffensiveNR />,
  () => <DefensiveNR />,
];

function NerdRanksTab() {
  const [innerTab, setInnerTab] = React.useState(0);
  const InnerComponent = NR_INNER_COMPONENTS[innerTab];
  return (
    <Box>
      <Box sx={{ borderBottom: '1px solid rgba(167,139,250,0.2)', mb: 3 }}>
        <Tabs
          value={innerTab}
          onChange={(_, v) => setInnerTab(v)}
          textColor="inherit"
          indicatorColor="secondary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minHeight: 40, fontWeight: 700, textTransform: 'none',
              fontSize: '0.82rem', px: 2, color: '#9eb8d0',
            },
            '& .Mui-selected': { color: '#a78bfa' },
            '& .MuiTabs-indicator': { backgroundColor: '#a78bfa' },
          }}
        >
          {NR_INNER_TABS.map(t => (
            <Tab key={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>
      <TierGate tier="free" feature="Nerd Ratings">
        <InnerComponent />
      </TierGate>
    </Box>
  );
}

// ── Tab 7: Glossary ───────────────────────────────────────────────────────────
function GlossaryTab() {
  return <MetricsGlossary />;
}

// ── Tab config ────────────────────────────────────────────────────────────────
const TABS = [
  { label: '📰 News',             id: 'news'       },
  { label: '🏈 Standings',        id: 'standings'  },
  { label: '📊 Player Stats',     id: 'players',   gate: 'free' },
  { label: '📈 Team Stats',       id: 'teams'      },
  { label: '⭐ Nerd Ranks',        id: 'nerd-ranks' },
  { label: '⚡ Advanced Metrics',  id: 'advanced-tools', gate: 'premium', chip: 'Nerds+' },
  { label: '📖 Glossary',         id: 'glossary'   },
];

const TAB_COMPONENTS = [NewsTab, StandingsTab, PlayerStatsTab, TeamStatsTab, NerdRanksTab, ComingSoonTab, GlossaryTab];

// ── Page ─────────────────────────────────────────────────────────────────────
export default function NFLPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabIdFromUrl = searchParams.get('tab');
  const initialTab = tabIdFromUrl ? Math.max(0, TABS.findIndex(t => t.id === tabIdFromUrl)) : 0;
  const [tab, setTab] = useState(initialTab);

  const handleTabChange = (newTab) => {
    setTab(newTab);
    const id = TABS[newTab]?.id;
    setSearchParams(newTab === 0 ? {} : { tab: id });
  };

  const Content = TAB_COMPONENTS[tab];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'transparent' }}>
      <ConstructionBanner />
      <Navbar />

      {/* Sub-nav */}
      <Box sx={{
        backgroundColor: '#060e1a',
        borderBottom: '1px solid #1a2d3d',
        position: 'sticky', top: NAVBAR_H, zIndex: 1100,
      }}>
        <Container maxWidth="xl" sx={{ px: { xs: 0.5, sm: 2 } }}>
          <Tabs value={tab} onChange={(_, v) => handleTabChange(v)}
            textColor="inherit" indicatorColor="primary"
            variant="scrollable" scrollButtons="auto"
            sx={{
              minHeight: SUBNAV_H,
              '& .MuiTab-root': {
                minHeight: SUBNAV_H, fontWeight: 600, textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.88rem' },
                px: { xs: 1, sm: 2 },
              },
            }}>
            {TABS.map(t => (
              <Tab key={t.id} label={
                t.chip ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {t.label}
                    <Chip label={t.chip} size="small" color="secondary"
                      sx={{ fontSize: '0.55rem', height: 15, pointerEvents: 'none', fontWeight: 800 }} />
                  </Box>
                ) : t.label
              } />
            ))}
          </Tabs>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Content />
      </Container>

      {/* Admin tier-switcher bar */}
      <AdminViewBar />
    </Box>
  );
}
